<template>
  <div class="dashboard-page animate-fade-in-up">
    <PageHeader icon="el-icon-s-grid" title="团队离职风险热力图" subtitle="实时监控团队人员风险状态，优先关注高风险员工" />

    <!-- 统计卡片 -->
    <div class="stats-row">
      <div class="stat-card stat-high">
        <div class="stat-bar"></div>
        <div class="stat-content">
          <div class="stat-label">高风险</div>
          <div class="stat-value">{{ highRisk.length }}</div>
          <div class="stat-desc">需立即介入</div>
        </div>
        <i class="el-icon-warning stat-icon"></i>
      </div>
      <div class="stat-card stat-medium">
        <div class="stat-bar"></div>
        <div class="stat-content">
          <div class="stat-label">中风险</div>
          <div class="stat-value">{{ mediumRisk.length }}</div>
          <div class="stat-desc">需持续关注</div>
        </div>
        <i class="el-icon-warning-outline stat-icon"></i>
      </div>
      <div class="stat-card stat-low">
        <div class="stat-bar"></div>
        <div class="stat-content">
          <div class="stat-label">低风险</div>
          <div class="stat-value">{{ lowRisk.length }}</div>
          <div class="stat-desc">状态稳定</div>
        </div>
        <i class="el-icon-circle-check stat-icon"></i>
      </div>
    </div>

    <!-- 风险看板 -->
    <div class="risk-board">
      <div class="risk-column">
        <div class="risk-column-header risk-header-high">
          <span class="risk-column-title">
            <i class="el-icon-warning"></i> 高风险
          </span>
          <span class="risk-column-count">{{ highRisk.length }} 人</span>
        </div>
        <div class="risk-column-body">
          <div v-if="highRisk.length === 0" class="risk-empty">
            <i class="el-icon-s-data"></i>
            <div>当前区间暂无人员</div>
          </div>
          <EmployeeCard v-for="emp in highRisk" :key="emp.employee_id" :employee="emp" color="red" />
        </div>
      </div>
      <div class="risk-column">
        <div class="risk-column-header risk-header-medium">
          <span class="risk-column-title">
            <i class="el-icon-warning-outline"></i> 中等风险
          </span>
          <span class="risk-column-count">{{ mediumRisk.length }} 人</span>
        </div>
        <div class="risk-column-body">
          <div v-if="mediumRisk.length === 0" class="risk-empty">
            <i class="el-icon-s-data"></i>
            <div>当前区间暂无人员</div>
          </div>
          <EmployeeCard v-for="emp in mediumRisk" :key="emp.employee_id" :employee="emp" color="orange" />
        </div>
      </div>
      <div class="risk-column">
        <div class="risk-column-header risk-header-low">
          <span class="risk-column-title">
            <i class="el-icon-circle-check"></i> 低风险 / 稳定
          </span>
          <span class="risk-column-count">{{ lowRisk.length }} 人</span>
        </div>
        <div class="risk-column-body">
          <div v-if="lowRisk.length === 0" class="risk-empty">
            <i class="el-icon-s-data"></i>
            <div>当前区间暂无人员</div>
          </div>
          <EmployeeCard v-for="emp in lowRisk" :key="emp.employee_id" :employee="emp" color="green" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EmployeeCard from '../components/EmployeeCard.vue'
import PageHeader from '../components/PageHeader.vue'

export default {
  name: 'Dashboard',
  components: { EmployeeCard, PageHeader },
  data() {
    return {
      highRisk: [],
      mediumRisk: [],
      lowRisk: [],
      loading: false
    }
  },
  mounted() {
    this.loadHeatmap()
  },
  activated() {
    // 从其他页面返回时重新刷新（配合 keep-alive 使用）
    this.loadHeatmap()
  },
  methods: {
    async loadHeatmap() {
      this.loading = true
      try {
        const taskId = this.$store.getters.currentTaskId
        const res = await this.$api.fetchHeatmap(taskId ? { task_id: taskId } : {})
        const data = res.data || {}
        this.highRisk = data.high_risk || []
        this.mediumRisk = data.medium_risk || []
        this.lowRisk = data.low_risk || []
      } catch (e) {
        this.$message.error('获取热力图数据失败：' + (e.message || '未知错误'))
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  max-width: 1400px;
  margin: 0 auto;
}
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-lg);
  margin-bottom: var(--space-2xl);
}
.stat-card {
  display: flex;
  align-items: center;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
  box-shadow: var(--shadow-sm);
  position: relative;
  overflow: hidden;
  transition: all var(--duration-normal) var(--ease-out);
}
.stat-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}
.stat-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}
.stat-high .stat-bar { background: var(--color-high); }
.stat-medium .stat-bar { background: var(--color-medium); }
.stat-low .stat-bar { background: var(--color-low); }
.stat-content {
  flex: 1;
  padding-left: var(--space-md);
}
.stat-label {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-xs);
}
.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1;
}
.stat-desc {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-top: var(--space-xs);
}
.stat-icon {
  font-size: 32px;
  color: var(--color-text-muted);
  opacity: 0.3;
}

.risk-board {
  display: flex;
  gap: var(--space-lg);
  overflow-x: auto;
}
.risk-column {
  flex: 1;
  min-width: 300px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}
.risk-column-header {
  padding: var(--space-md) var(--space-lg);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.risk-header-high { background: var(--color-high-bg); }
.risk-header-medium { background: var(--color-medium-bg); }
.risk-header-low { background: var(--color-low-bg); }
.risk-column-title {
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}
.risk-header-high .risk-column-title { color: var(--color-high-text); }
.risk-header-medium .risk-column-title { color: var(--color-medium-text); }
.risk-header-low .risk-column-title { color: var(--color-low-text); }
.risk-column-count {
  font-weight: 600;
  font-size: 13px;
}
.risk-header-high .risk-column-count { color: var(--color-high); }
.risk-header-medium .risk-column-count { color: var(--color-medium); }
.risk-header-low .risk-column-count { color: var(--color-low); }
.risk-column-body {
  padding: var(--space-md);
  min-height: 300px;
}
.risk-empty {
  text-align: center;
  padding: var(--space-3xl) 0;
  color: var(--color-text-muted);
}
.risk-empty i {
  font-size: 48px;
  margin-bottom: var(--space-md);
  display: block;
}
</style>
